import {DatePicker} from '@github-ui/date-picker'
import type {RangeSelection} from '@github-ui/date-picker/types'
import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {useSyncedState} from '@github-ui/use-synced-state'
import {TrashIcon} from '@primer/octicons-react'
import {IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import {addDays, parseISO, subYears} from 'date-fns'
import isEqual from 'lodash-es/isEqual'
import {type ChangeEvent, useMemo, useRef} from 'react'

import type {IterationInterval} from '../../../../api/columns/contracts/iteration'
import {AutosizeTextInput} from '../../../../components/common/autosize-text-input'
import {BorderlessTextInput} from '../../../../components/common/borderless-text-input'
import {EmojiAutocomplete} from '../../../../components/common/emoji-autocomplete'
import {IterationRowLabel} from '../../../../components/fields/iteration/iteration-label'
import {replaceShortCodesWithEmojis} from '../../../../helpers/emojis'
import {
  intervalAsRangeSelection,
  intervalDatesDescription,
  intervalDurationDescription,
  type IterationLabelType,
  rangeSelectionAsInterval,
} from '../../../../helpers/iterations'
import {IterationActionsMenu} from './iteration-actions-menu'
import {DiffValue} from './iteration-diff-value'
import styles from './iteration-row-skeleton.module.css'

/** Trash can icon to click to delete a row. */
function DeleteButton({onClick}: {onClick: () => void}) {
  return (
    <IconButton
      icon={TrashIcon}
      aria-label="Remove item"
      onClick={onClick}
      className={styles.IconButton}
      {...testIdProps('delete-iteration')}
    />
  )
}

interface TitleInputProps {
  value: string
  onChange?: (newValue: string) => void
}

/** Editable title for iterations. */
function TitleInput({value, onChange}: TitleInputProps) {
  const [inputValue, setInputValue] = useSyncedState(value)
  const reset = () => setInputValue(value)
  const ref = useRef<HTMLInputElement | null>(null)

  const apply = () => {
    const trimmed = replaceShortCodesWithEmojis(inputValue.trim())
    if (!trimmed) {
      reset()
    } else if (trimmed !== value) {
      onChange?.(trimmed)
    }
  }

  const titleInputComposingProps = useIgnoreKeyboardActionsWhileComposing(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Enter') apply()
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      else if (event.key === 'Escape') reset()
      else return

      event.preventDefault()
      // Timeout to let state update first and avoid blur event triggering save
      // eslint-disable-next-line github/no-blur
      setTimeout(() => ref.current?.blur(), 10)
    },
  )

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={() => ref.current?.focus()} className={styles.editableContainer}>
      <EmojiAutocomplete containerClassName={styles.titleContainer}>
        <AutosizeTextInput
          as={BorderlessTextInput}
          autoComplete="off"
          className={styles.textInput}
          value={inputValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          onBlur={apply}
          aria-label="Edit iteration name"
          ref={ref}
          {...titleInputComposingProps}
          {...testIdProps('iteration-title')}
        />
      </EmojiAutocomplete>
    </div>
  )
}

interface IntervalDatePickerProps {
  value: IterationInterval
  onChange: (newValue: IterationInterval) => void
  minDate: Date
}

/** Range-based datepicker for updating iteration / break intervals. */
function IntervalDatePicker({value, onChange, minDate}: IntervalDatePickerProps) {
  const onRangeChange = (selection?: RangeSelection | null) => {
    if (selection) onChange(rangeSelectionAsInterval(selection))
  }
  const rangeSelection = useMemo(() => intervalAsRangeSelection(value), [value])

  return (
    <DatePicker
      variant="range"
      value={rangeSelection}
      onChange={onRangeChange}
      view="2-month"
      minDate={minDate}
      confirmation
      confirmUnsavedClose
      anchor={p => (
        <Link
          muted
          {...p}
          as="button"
          aria-label="Edit date range"
          className={styles.inheritColorLink}
          {...testIdProps('iteration-or-break-dates')}
        >
          {intervalDatesDescription(value)}
        </Link>
      )}
    />
  )
}

interface IterationRowSkeletonProps {
  /** The local title of the item. Leave empty to hide the title. */
  localTitle?: string
  /** The server title of the item (title since last save). Leave empty to hide the title. */
  originalTitle?: string
  /** Called when the item title changes. */
  onTitleChange?: (newName: string) => void

  /** The local date range of the item, with any unsaved changes. */
  localInterval: IterationInterval
  /** The server-side date range of the item, before any unsaved changes. */
  originalInterval?: IterationInterval
  /** The interval before this one (with any unsaved changes). */
  localPreviousInterval?: IterationInterval
  /** Called when the user edits the interval. */
  onIntervalChange: (newInterval: IterationInterval) => void

  labelType: IterationLabelType
  onRemove: () => void

  /** Callback to insert a break above this iteration */
  onInsertBreakAbove?: () => void
  /** Callback to insert a break below this iteration */
  onInsertBreakBelow?: () => void
}

/**
 * Shared functionality & layout for all iteration row types (breaks and iterations).
 */
export function IterationRowSkeleton({
  localTitle: localTitle = '',
  originalTitle: originalTitle = '',
  localInterval,
  localPreviousInterval,
  originalInterval,
  labelType,
  onRemove,
  onTitleChange,
  onIntervalChange,
  onInsertBreakAbove,
  onInsertBreakBelow,
}: IterationRowSkeletonProps) {
  const isDirty = localTitle !== originalTitle || !isEqual(localInterval, originalInterval)

  const minAllowedDate = useMemo(
    () =>
      localPreviousInterval
        ? addDays(parseISO(localPreviousInterval.startDate), localPreviousInterval.duration)
        : subYears(new Date(), 1),
    [localPreviousInterval],
  )

  return (
    <li className={styles.iterationRowContainer}>
      <div
        className={clsx(
          styles.baseIteration,
          isDirty && styles.isDirty,
          labelType === 'break' ? styles.withBreak : styles.withIteration,
        )}
      >
        <IterationRowLabel labelType={labelType} className={clsx(localTitle && styles.IterationRowSkeletonLabel)} />

        <div className={styles.columnFlexContainer}>
          {(localTitle || originalTitle) && (
            <div className={styles.rowFlexContainer}>
              <DiffValue
                originalValue={originalTitle}
                updatedValue={localTitle}
                testId="iteration-title-diff"
                renderUpdatedValue={_ => <TitleInput value={localTitle} onChange={onTitleChange} />}
                className={styles.DiffValue}
              />
            </div>
          )}

          <div className={styles.rowFlexContainer}>
            <DiffValue
              originalValue={intervalDatesDescription(originalInterval)}
              updatedValue={intervalDatesDescription(localInterval)}
              testId="iteration-dates-diff"
              renderOriginalValue={() => (
                <>
                  <span className={styles.semiboldText}>{intervalDurationDescription(originalInterval)}</span>
                  &nbsp;&nbsp;
                  <span>{intervalDatesDescription(originalInterval)}</span>
                </>
              )}
              renderUpdatedValue={() => (
                <>
                  <span className={styles.semiboldText}>{intervalDurationDescription(localInterval)}</span>&nbsp;&nbsp;
                  <IntervalDatePicker value={localInterval} onChange={onIntervalChange} minDate={minAllowedDate} />
                </>
              )}
            />
          </div>
        </div>

        <DeleteButton onClick={onRemove} />
        <IterationActionsMenu onInsertBreakAbove={onInsertBreakAbove} onInsertBreakBelow={onInsertBreakBelow} />
      </div>
    </li>
  )
}
